import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import { useLocation, useOutletContext } from "react-router-dom";
import { helpTextIcon } from "helpers/helpText";
import ErrorAlert from "components/ui/ErrorAlert";
import { Tooltip } from "react-tooltip";
import { fetchPeriods, getActivePeriod } from "store/periodSlice";
import { useTranslation } from "translation/useTranslation";
import { toast } from "helpers/toast";
import "react-toastify/dist/ReactToastify.min.css";
import { LOB_MODULE, DB_MODULE } from "../../constants";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import { isSameModule, normalizeModule } from "helpers/module";
import PeriodTimeline from "components/PeriodManagement/PeriodTimeline";
import PeriodDetail from "components/PeriodManagement/PeriodDetail";
import PeriodCreateForm from "components/PeriodManagement/PeriodCreateForm";
import PeriodEditForm from "components/PeriodManagement/PeriodEditForm";
import { usePeriodStrings } from "translation/periodManagement";
import { Modal } from "components/ui";
import {
  chainOf,
  isLocked,
  latestIn,
  moduleOf,
  openPeriodLimitReachedIn,
  openPeriodLimitReplacementsFor,
  preserveOpenPeriodLimitErrorMessage,
} from "helpers/periods";
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

export default function PeriodManagement() {
  const location = useLocation();
  const dispatch = useDispatch();

  const activePeriod = useSelector(getActivePeriod);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { editableTranslate, translate } = useTranslation();
  const { pt, ps } = usePeriodStrings();

  const { helps } = useOutletContext();

  const [periods, setPeriods] = useState([]);
  const [licensedModules, setLicensedModules] = useState([]);

  // null = overview, 'new' = create, a period object = edit
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  // Which lane's proposal bar opened the create form.
  const [createModule, setCreateModule] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [chainAnchorId, setChainAnchorId] = useState(null);

  const [confirmAction, setConfirmAction] = useState(null);

  const [counter, setCounter] = useState(0);
  const [countNew, setCountNew] = useState(0);

  useEffect(() => {
    setSelectedPeriod(null);
  }, [counter]);

  useEffect(() => {
    setCounter(prev => prev + 1);
  }, [location]);

  const periodHelp = useMemo(() => {
    if (helps) {
      return helps.filter(h => h.key === 'period-management-intro')[0] || null;
    }

    return null;
  }, [helps]);

  useEffect(() => {
    fetchLicense();
    fetchAllPeriods();
  }, []);

  useEffect(() => {
    if (countNew > 0) {
      setTimeout(pollForPeriods, 10000);
    }
  }, [countNew]);

  /**
   * One lane per module the customer is licensed for. Without a usable license
   * answer the modules that actually carry periods are the next best truth,
   * because a lane the customer cannot use is worse than a missing one.
   */
  const modules = useMemo(() => {
    const fromLicense = (licensedModules || []).map(m => normalizeModule(m, null)).filter(Boolean);
    const fromPeriods = periods.map(moduleOf).filter(Boolean);
    const available = fromLicense.length > 0 ? fromLicense : fromPeriods;

    return [LOB_MODULE, DB_MODULE].filter(m => available.includes(m));
  }, [licensedModules, periods]);

  const selected = useMemo(
    () => periods.find(p => p.id === selectedId) || null,
    [periods, selectedId]
  );

  // Keep a selection at all times: the default period of the leading module.
  useEffect(() => {
    if (periods.length === 0 || (selectedId && periods.some(p => p.id === selectedId))) return;

    const primary = modules[0];
    const home = periods.find(p => p.is_active_period && isSameModule(moduleOf(p), primary))
      || chainOf(periods, primary)[0]
      || periods[0];

    setSelectedId(home?.id ?? null);
    setChainAnchorId(home?.id ?? null);
  }, [periods, modules, selectedId]);

  const fetchLicense = async () => {
    try {
      const { data, status } = await api.get('/common/license/state');
      if (status === 200) {
        setLicensedModules(data?.data?.restrictions?.modules || []);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch license'));
    }
  };

  const fetchAllPeriods = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, status } = await api.get('/periods/all?status=live,draft,archived');
      if (status === 200) {
        setPeriods(data.data.periods);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch periods'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPeriod = (period) => {
    setSelectedId(period.id);
    setChainAnchorId(chainAnchorId ?? period.id);
  };

  const handleSelectFromLineage = (period) => {
    setSelectedId(period.id);
  };

  const handleOpenCreate = (module) => {
    if (openPeriodLimitReachedIn(periods, module)) {
      setError(ps('period_management.ghost.blocked', openPeriodLimitReplacementsFor(periods, module)));
      return;
    }

    setError(null);
    // The lane decides the module. Carry it, there is no picker on the form.
    setCreateModule(module);
    setSelectedPeriod('new');
  };

  const handlePerformAction = async (periodId, action) => {
    const periodFound = periods.find(p => p.id === periodId);

    if (action === 'set_edit') {
      setSelectedPeriod(periodFound);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const { status } = await api.post(`/periods/action/${periodId}`, {
        action: action,
        period_name: periodFound?.name || ''
      });

      if (status === 200) {
        fetchAllPeriods();

        if (['set_deleted', 'set_archived', 'set_live'].includes(action)) {
          dispatch(fetchPeriods());
        }

        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      const formattedError = errorMessage(err, `perform action ${action} on period`);
      setError(preserveOpenPeriodLimitErrorMessage(err, formattedError));
    } finally {
      setLoading(false);
    }
  };

  const lockContext = useMemo(
    () => ({ currentPeriodName: activePeriod, periods }),
    [activePeriod, periods]
  );

  /**
   * The menu offers exactly what the status allows. Locked entries are left out
   * rather than greyed out, their reason is spelled out in the detail area.
   * Reactivating an archived period is the same set_live the API already
   * accepts, it simply had no button.
   */
  const menuItems = useMemo(() => {
    if (!selected || selected.ready === false) return [];

    const byStatus = {
      draft: [
        { key: 'set_live', action: 'set_live' },
        { key: 'set_edit', action: 'set_edit' },
        { key: 'set_archived', action: 'set_archived', confirm: true },
        { key: 'set_deleted', action: 'set_deleted', confirm: true, danger: true },
      ],
      live: [
        { key: 'make_default', action: 'make_default' },
        { key: 'set_edit', action: 'set_edit' },
        { key: 'set_draft', action: 'set_draft', confirm: true },
        { key: 'set_archived', action: 'set_archived', confirm: true },
      ],
      archived: [
        { key: 'reactivate', action: 'set_live' },
        { key: 'set_edit', action: 'set_edit' },
      ],
    };

    return (byStatus[selected.status] || [])
      .filter(entry => !(entry.key === 'make_default' && selected.is_active_period))
      .filter(entry => !isLocked(selected, entry.action, lockContext))
      .map(entry => ({
        key: entry.key,
        label: ps(`period_management.action.${entry.key}`),
        danger: entry.danger,
        onClick: () => (entry.confirm
          ? setConfirmAction({ ...entry, period: selected })
          : handlePerformAction(selected.id, entry.action)),
      }));
  }, [selected, periods, lockContext]);

  const refetchAll = () => {
    fetchAllPeriods();
    dispatch(fetchPeriods());
  };

  // Dates arrive from the form in the same Y-m-d the API stores them in.
  const validateEvaluationPeriod = (values, module, id = null) => {
    const startDate = dayjs(values?.evaluation_period_start_date, 'YYYY-MM-DD');
    const endDate = dayjs(values?.evaluation_period_end_date, 'YYYY-MM-DD');

    if (periods) {
      for (const item of periods) {
        if ((id ? id !== item.id : true) && isSameModule(module, moduleOf(item)) && item?.evaluation_period_start_date && item?.evaluation_period_end_date) {
          const itemStartDate = dayjs(item?.evaluation_period_start_date, 'YYYY-MM-DD');
          const itemEndDate = dayjs(item?.evaluation_period_end_date, 'YYYY-MM-DD');

          if (startDate.isBetween(itemStartDate, itemEndDate, null, '[]') ||
            endDate.isBetween(itemStartDate, itemEndDate, null, '[]') ||
            itemStartDate.isBetween(startDate, endDate, null, '[]') ||
            itemEndDate.isBetween(startDate, endDate, null, '[]')
          ) {
            return item;
          }
        }
      }
    }

    return true;
  };

  const pollForPeriods = () => {
    const POLL_INTERVAL = 10000; // 10 seconds
    const MAX_POLL_DURATION = 60000; // 1 minute in milliseconds
    const startTime = Date.now();

    const checkPeriods = async () => {
      try {
        dispatch(fetchPeriods()).then(() => {
          const allReady = periods.every((period) => period.ready === true);
          const elapsedTime = Date.now() - startTime;

          if (allReady) {
            setCountNew(0);
            refetchAll();
          } else if (elapsedTime < MAX_POLL_DURATION) {
            setTimeout(checkPeriods, POLL_INTERVAL);
          }
        });
      } catch (error) {
        console.error("Error while polling periods:", error);
      }
    };

    checkPeriods();
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <div className="d-flex justify-content-between">
            <div>
              <h4>
                {selectedPeriod === 'new'
                  ? pt('period_management.create.title')
                  : selectedPeriod
                    ? pt('period_management.edit.title', { name: selectedPeriod?.name })
                    : editableTranslate('global.menu.settings.period_management', 'Bewertungszeiträume')}
              </h4>
              <p className="section-description">
                {
                  selectedPeriod === 'new'
                    ? pt(latestIn(periods, createModule || modules[0])
                      ? 'period_management.create.subtitle'
                      : 'period_management.create.subtitle_first')
                    : selectedPeriod
                      ? editableTranslate("period_management.edit.subtitle", 'Periodendetails aktualisieren')
                      : pt(modules.length > 1
                        ? 'period_management.intro.both_modules'
                        : 'period_management.intro.single_module')
                }
                {
                  (periodHelp && (selectedPeriod === 'new' || !selectedPeriod)) ? helpTextIcon(periodHelp?.key, periodHelp?.short_text) : ''
                }
              </p>
            </div>
          </div>
          {
            error && <div>
              <ErrorAlert error={error} isHtml={true} onClose={() => setError(null)} />
            </div>
          }
          {
            !selectedPeriod ? <div className="mt-3">
              {
                loading ? <div className="text-center py-5" data-testid="loading">
                  <div className="spinner-border text-primary" role="status" />
                </div> : <>
                  <PeriodTimeline
                    periods={periods}
                    modules={modules}
                    selectedId={selectedId}
                    onSelect={handleSelectPeriod}
                    onCreate={handleOpenCreate}
                  />
                  <PeriodDetail
                    period={selected}
                    periods={periods}
                    menuItems={menuItems}
                    chainAnchorId={chainAnchorId}
                    lockContext={lockContext}
                    onSelect={handleSelectFromLineage}
                  />
                </>
              }
            </div> : <div className="mt-2">
              {
                selectedPeriod === 'new' ? <PeriodCreateForm
                  periods={periods}
                  module={createModule || modules[0]}
                  setError={setError}
                  onCancel={() => setSelectedPeriod(null)}
                  onCreated={() => { setSelectedPeriod(null); setCountNew(c => c + 1); refetchAll(); }}
                /> :
                  <PeriodEditForm
                    period={selectedPeriod}
                    periods={periods}
                    activePeriod={activePeriod}
                    validateEvaluationPeriod={validateEvaluationPeriod}
                    setError={setError}
                    refetchAll={refetchAll}
                    onCancel={() => { setSelectedPeriod(null); setError(null); }}
                    onSaved={() => setSelectedPeriod(null)}
                  />
              }
            </div>
          }

          <Modal
            open={!!confirmAction}
            danger={!!confirmAction?.danger}
            loading={loading}
            title={confirmAction ? ps(`period_management.confirm.${confirmAction.action}.title`) : ''}
            okLabel={confirmAction ? ps(`period_management.confirm.${confirmAction.action}.ok`) : ''}
            cancelLabel={ps('period_management.confirm.cancel')}
            onClose={() => setConfirmAction(null)}
            onConfirm={() => {
              const pending = confirmAction;
              setConfirmAction(null);
              handlePerformAction(pending.period.id, pending.action);
            }}
          >
            {confirmAction
              ? ps(`period_management.confirm.${confirmAction.action}.body`, { name: confirmAction.period.name })
              : ''}
          </Modal>
        </div>

        <Tooltip id="period-id-tooltip" place="bottom" clickable />
      </div>
    </>
  );
}
